import { groupBy, isEqual, maxBy } from 'lodash';
import React, {
  ChangeEvent,
  FC,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
} from 'reactstrap';
import { useDeepCompareEffect } from 'use-deep-compare';
import ObjectsDropdown from '../Dropdowns/ObjectsDropdown';
import ReactTagsInput from '../Inputs/ReactTagsInput';
import {
  Filterable,
  isRangeOperator,
  TCondition,
  TConditionOperator,
  TGroup,
  TGroupOerator,
} from './types';

const DEFAULT_VALUE: TGroup = {
  index: 0,
  type: 'GROUP',
  operator: 'OR',
  values: [
    {
      type: 'CONDITION',
      key: undefined,
      values: [],
      operator: 'EQ',
      index: 100,
    },
  ],
};

const FILTERABLE_TAGS_ARE_EQUAL = (
  tag1: Filterable,
  tag2: Filterable
): boolean => {
  return isEqual(tag1.filterObject, tag2.filterObject);
};

const IDENTITY = (x: Filterable): Filterable => x;

// condition keys (mapped to their description) holding numeric values: they
// additionally offer the range operators, accept a typed number as value,
// and are offered even when no suggestion carries them
export type NumericKeys = Record<string, string>;
const NO_NUMERIC_KEYS: NumericKeys = {};

interface Props<T extends Filterable> {
  suggestions: T[];
  numericKeys?: NumericKeys;
  value?: TGroup;
  suggestionForConditionConverter?: (s: Filterable) => Filterable;
  tagsAreEqual?: (tag1: Filterable, tag2: Filterable) => boolean;
  onChange: (value: TGroup | undefined) => void;
  onValidChange?: (message: string | null) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  required?: boolean;
  searchBarPlaceholder?: string;
  className?: string;
  minStrokes?: number;
}

const EMPTY_LIST = [];
const AdvancedFilter = <T extends Filterable>({
  suggestions,
  numericKeys = NO_NUMERIC_KEYS,
  value,
  suggestionForConditionConverter = IDENTITY,
  tagsAreEqual = FILTERABLE_TAGS_ARE_EQUAL,
  onChange,
  onValidChange,
  size = 'md',
  disabled = false,
  required = false,
  searchBarPlaceholder,
  className = '',
  minStrokes,
}: PropsWithChildren<Props<T>>): ReactElement<unknown, string> | null => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  useDeepCompareEffect(() => {
    if (required) {
      const isValid = value && validateGroup(value).length === 0;
      setValidationMessage(
        isValid
          ? null
          : formatMessage({
              id: 'app.views.widgets.filters.advanced_filter.required',
              defaultMessage: 'Please select at least a valid condition.',
            })
      );
    }
  }, [formatMessage, required, value]);

  const searchBarPlaceholderText = useMemo(() => {
    if (searchBarPlaceholder) {
      return searchBarPlaceholder;
    }
    return intl.formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.search',
      defaultMessage: 'Type here to search for a condition...',
    });
  }, [intl, searchBarPlaceholder]);

  useEffect(() => {
    onValidChange?.(validationMessage);
  }, [onValidChange, validationMessage]);

  const handleOnChange = useCallback(
    (value: TGroup) => {
      const validationErrors = validate(value, intl);

      setValidationMessage(
        validationErrors.length > 0 ? validationErrors.join(' ') : null
      );
      onChange(value);
    },
    [intl, onChange]
  );

  const onHelperSelectedCallback = useCallback(
    (values) => {
      handleOnChange(
        addCondition(
          value ?? DEFAULT_VALUE,
          0,
          suggestionForConditionConverter,
          values,
          true
        ) as TGroup
      );
    },
    [handleOnChange, suggestionForConditionConverter, value]
  );

  const handleOnDelete = useCallback(
    (_idx: number) => {
      setValidationMessage(null);
      onChange(undefined);
    },
    [onChange]
  );

  // opens the expression builder with a blank condition to fill in, for
  // building a filter by hand instead of starting from a searched suggestion
  const handleShowBuilder = useCallback(() => {
    handleOnChange(DEFAULT_VALUE);
  }, [handleOnChange]);

  const suggestionsForCondition = useMemo(() => {
    return suggestions.map(suggestionForConditionConverter);
  }, [suggestionForConditionConverter, suggestions]);

  // check for invalid conditions
  // if the key of the condition is not in the suggestions, it will be
  // ingorend and sent a handleOnChange with the sanitized value
  const invalidConditions: TCondition[] = useMemo(() => {
    return value
      ? findConditionsNotMatchingSuggestions(value, suggestions, numericKeys)
      : [];
  }, [numericKeys, suggestions, value]);

  useEffect(() => {
    if (value && invalidConditions.length > 0) {
      const sanitizedValue = invalidConditions.reduce(
        (_, invalidCondition) => deleteNestedItem(_, invalidCondition.index),
        value
      );
      handleOnChange(sanitizedValue);
    }
  }, [handleOnChange, invalidConditions, value]);

  return (
    <Row className={'ps-0 ' + className}>
      {!disabled && !value && (
        <Col className="col-auto pe-2">
          <Button
            role="button"
            aria-label={formatMessage({
              id: 'app.views.widgets.filters.advanced_filter.show_builder_button.aria_label',
              defaultMessage: 'Build a filter',
            })}
            onClick={handleShowBuilder}
            size={size}
            color="light"
          >
            <i className="fe fe-plus" />
          </Button>
        </Col>
      )}
      {!disabled && (
        <Col className={value ? '' : 'ps-0'}>
          <ReactTagsInput
            disabled={disabled}
            className="w-100 m-0 ps-0"
            value={EMPTY_LIST}
            allowNew={false}
            suggestions={suggestions}
            placeholder={searchBarPlaceholderText}
            callback={onHelperSelectedCallback}
            useTagCards={true}
            disableLengthCheck={true}
            tagsAreEqual={tagsAreEqual}
            clearTagsAfterCallback={true}
            minQueryLength={minStrokes}
          />
        </Col>
      )}
      {value && (
        <Group
          disabled={disabled}
          className="ps-4 pt-2 pe-0"
          value={value}
          suggestionForConditionConverter={suggestionForConditionConverter}
          suggestions={suggestionsForCondition}
          numericKeys={numericKeys}
          onChange={handleOnChange}
          onDelete={handleOnDelete}
          tagsAreEqual={tagsAreEqual}
          size={size}
          minStrokes={minStrokes}
        />
      )}
    </Row>
  );
};

const GROUP_OPERATORS = (
  formatMessage: IntlShape['formatMessage']
): {
  id: TGroupOerator;
  name: string;
}[] => [
  {
    id: 'OR',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.group.or',
      defaultMessage: 'Match at least one condition',
    }),
  },
  {
    id: 'AND',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.group.and',
      defaultMessage: 'Match every condition',
    }),
  },
];

interface GroupProps<T extends Filterable> {
  disabled: boolean;
  value: TGroup;
  suggestions: T[];
  numericKeys?: NumericKeys;
  suggestionForConditionConverter: (s: Filterable) => Filterable;
  onDelete: (idx: number) => void;
  tagsAreEqual?: (tag1: Filterable, tag2: Filterable) => boolean;
  onChange: (value: TGroup) => void;
  depth?: number;
  size: 'sm' | 'md' | 'lg';
  className?: string;
  minStrokes?: number;
}

export const Group = <T extends Filterable>({
  depth = 0,
  value,
  suggestions,
  numericKeys = NO_NUMERIC_KEYS,
  suggestionForConditionConverter,
  onChange,
  onDelete,
  tagsAreEqual,
  size,
  className = '',
  disabled,
  minStrokes,
}: PropsWithChildren<GroupProps<T>>): ReactElement<unknown, string> | null => {
  const { formatMessage } = useIntl();

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const toggle = useCallback(
    () => setDropdownOpen((prevState: boolean) => !prevState),
    []
  );

  const handleChangeOperator = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (value.operator === e.target.value) {
        return;
      }
      onChange({
        ...value,
        operator: e.target.value as TGroupOerator,
      } as TGroup);
    },
    [onChange, value]
  );

  const handleAddCondition = useCallback(() => {
    onChange(addCondition(value, depth, suggestionForConditionConverter));
  }, [depth, onChange, suggestionForConditionConverter, value]);

  const handleAddGroup = useCallback(() => {
    onChange(addGroup(value, depth));
  }, [depth, onChange, value]);

  const handleDelete = useCallback(() => {
    onDelete(value.index);
  }, [onDelete, value.index]);

  const handleChangeItem = useCallback(
    (changedValue: TCondition | TGroup) => {
      if (!value.values.find((item) => item.index === changedValue.index)) {
        onChange(value);
        return;
      }
      if (value.values.find((item) => item.index === changedValue.index)) {
        onChange({
          ...value,
          values: value.values.map((item) =>
            item.index === changedValue.index ? changedValue : item
          ),
        });
        return;
      }
      onChange(value);
      return;
    },
    [onChange, value]
  );

  const handleDeletedItem = useCallback(
    (idx: number) => {
      onChange(deleteItem(value, idx));
    },
    [onChange, value]
  );

  return (
    <Row
      role="group"
      aria-label={formatMessage({
        id: 'app.views.widgets.filters.advanced_filter.conditions.aria_label',
        defaultMessage: 'Advanced filter conditions',
      })}
      className={className + ' row justify-content-start'}
    >
      <Col className="col-auto p-0 ms-0">
        <Col className="p-0 pe-1">
          <ObjectsDropdown
            buttonRole="button"
            buttonAriaLabel={formatMessage({
              id: 'app.views.widgets.filters.advanced_filter.group_operator_button.aria_label',
              defaultMessage: 'Group operator',
            })}
            objects={GROUP_OPERATORS(formatMessage)}
            value={value.operator}
            size={size}
            onChange={handleChangeOperator}
            disabled={disabled}
          />
        </Col>
      </Col>
      <Col className="p-0">
        {!disabled && (
          <Dropdown
            color="light"
            isOpen={dropdownOpen}
            size={size}
            toggle={toggle}
          >
            <DropdownToggle
              aria-label={formatMessage({
                id: 'app.views.widgets.filters.advanced_filter.add_condition_button.aria_label',
                defaultMessage: 'Add condition',
              })}
              color="info"
              size={size}
              outline
            >
              <FormattedMessage
                id="app.views.widgets.filters.advanced_filter.add"
                defaultMessage="Add..."
              />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={handleAddCondition}>
                <FormattedMessage
                  id="app.views.widgets.filters.advanced_filter.condition"
                  defaultMessage="Condition"
                />
              </DropdownItem>
              <DropdownItem onClick={handleAddGroup}>
                <FormattedMessage
                  id="app.views.widgets.filters.advanced_filter.group_of_conditions"
                  defaultMessage="Group of conditions"
                />
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        )}
      </Col>
      <Col className="col-auto p-0 pe-1">
        {!disabled && (
          <Button
            role="button"
            aria-label={formatMessage({
              id: 'app.views.widgets.filters.advanced_filter.delete_all_conditions_button.aria_label',
              defaultMessage: 'Delete all conditions',
            })}
            onClick={handleDelete}
            size={size}
            outline
            color="light"
          >
            <i style={{ color: 'red' }} className="fe fe-trash-2" />
          </Button>
        )}
      </Col>
      {value.values.map((item: TCondition | TGroup, index: number) => {
        return (
          <Row className="ms-3 pt-1" key={index}>
            <Col>
              {item.type === 'CONDITION' ? (
                <Condition
                  disabled={disabled}
                  depth={depth + 1}
                  value={item}
                  suggestions={suggestions}
                  numericKeys={numericKeys}
                  onDelete={handleDeletedItem}
                  onChange={handleChangeItem}
                  tagsAreEqual={tagsAreEqual}
                  size={size}
                  minStrokes={minStrokes}
                />
              ) : (
                <Group
                  disabled={disabled}
                  depth={depth + 1}
                  value={item}
                  suggestions={suggestions}
                  numericKeys={numericKeys}
                  suggestionForConditionConverter={
                    suggestionForConditionConverter
                  }
                  tagsAreEqual={tagsAreEqual}
                  onChange={handleChangeItem}
                  onDelete={handleDeletedItem}
                  size={size}
                  minStrokes={minStrokes}
                />
              )}
            </Col>
          </Row>
        );
      })}
    </Row>
  );
};

const CONDITION_OPERATORS = (
  formatMessage: IntlShape['formatMessage']
): {
  id: TConditionOperator;
  name: string;
}[] => [
  {
    id: 'EQ',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.condition.eq',
      defaultMessage: 'IS',
    }),
  },
  {
    id: 'NE',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.condition.ne',
      defaultMessage: 'IS NOT',
    }),
  },
];

const NUMERIC_CONDITION_OPERATORS = (
  formatMessage: IntlShape['formatMessage']
): {
  id: TConditionOperator;
  name: string;
}[] => [
  ...CONDITION_OPERATORS(formatMessage),
  {
    id: 'GT',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.condition.gt',
      defaultMessage: 'IS GREATER THAN',
    }),
  },
  {
    id: 'GTE',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.condition.gte',
      defaultMessage: 'IS AT LEAST',
    }),
  },
  {
    id: 'LT',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.condition.lt',
      defaultMessage: 'IS LESS THAN',
    }),
  },
  {
    id: 'LTE',
    name: formatMessage({
      id: 'app.views.widgets.filters.advanced_filter.condition.lte',
      defaultMessage: 'IS AT MOST',
    }),
  },
];

const Condition: FC<{
  depth: number;
  value: TCondition;
  suggestions: Filterable[];
  numericKeys?: NumericKeys;
  onDelete: (idx: number) => void;
  tagsAreEqual?: (tag1: Filterable, tag2: Filterable) => boolean;
  onChange: (value: TCondition) => void;
  disabled: boolean;
  size: 'sm' | 'md' | 'lg';
  minStrokes?: number;
}> = ({
  value,
  suggestions,
  numericKeys = NO_NUMERIC_KEYS,
  onDelete,
  tagsAreEqual,
  onChange,
  size,
  disabled,
  minStrokes,
}) => {
  const { formatMessage } = useIntl();

  const isNumericKey = !!value.key && value.key in numericKeys;
  const isRange = isRangeOperator(value.operator);

  const handleDelete = useCallback(() => {
    onDelete(value.index);
  }, [onDelete, value.index]);

  const handleChangeKey = useCallback(
    (changedValue: ChangeEvent<HTMLInputElement>) => {
      const key = changedValue.target.value;
      onChange({
        ...value,
        key,
        // reset values since key has changed
        values: [],
        // range operators only make sense for numeric keys
        operator:
          key in numericKeys || !isRangeOperator(value.operator)
            ? value.operator
            : 'EQ',
      });
    },
    [numericKeys, value, onChange]
  );

  const handleChangeOperator = useCallback(
    (changedValue: ChangeEvent<HTMLInputElement>) => {
      const operator = changedValue.target.value as TConditionOperator;
      if (value.operator === operator) {
        return;
      }
      onChange({
        ...value,
        operator,
        // a range compares against a single bound
        values: isRangeOperator(operator)
          ? value.values.slice(0, 1)
          : value.values,
      });
    },
    [onChange, value]
  );

  const handleChangeValues = useCallback(
    (changedValues: Filterable[]) => {
      onChange({
        ...value,
        values: changedValues,
      });
    },
    [onChange, value]
  );

  const groupedSuggestions = useMemo(
    () => groupBy(suggestions, (s) => s.filterObject.key),
    [suggestions]
  );

  const conditionKeys = useMemo(
    () => [
      ...Object.entries(groupedSuggestions).map(([key, values]) => ({
        id: key,
        name: values[0].filterObject.keyDescription,
      })),
      ...Object.entries(numericKeys)
        .filter(([key]) => !(key in groupedSuggestions))
        .map(([key, keyDescription]) => ({ id: key, name: keyDescription })),
    ],
    [groupedSuggestions, numericKeys]
  );

  const conditionOperators = useMemo(
    () =>
      isNumericKey
        ? NUMERIC_CONDITION_OPERATORS(formatMessage)
        : CONDITION_OPERATORS(formatMessage),
    [formatMessage, isNumericKey]
  );

  const suggestionsForKey = useMemo(() => {
    return value.key ? (groupedSuggestions[value.key] ?? []) : [];
  }, [value.key, groupedSuggestions]);

  const isConditionMatchingSuggestion = useMemo(() => {
    return (
      !value?.values?.length ||
      conditionKeys.some((conditionKey) => conditionKey.id === value.key)
    );
  }, [conditionKeys, value.key, value.values?.length]);

  // If the condition is not matching any suggestion, we do not render it
  // because it has no valid key
  if (!isConditionMatchingSuggestion) {
    return null;
  }

  return (
    <Row>
      <Col className="col-auto p-0 pe-1">
        <ObjectsDropdown
          buttonRole="button"
          buttonAriaLabel={formatMessage({
            id: 'app.views.widgets.filters.advanced_filter.field_selector_button.aria_label',
            defaultMessage: 'Field selector',
          })}
          disabled={disabled}
          objects={conditionKeys}
          value={value.key}
          onChange={handleChangeKey}
          size={size}
        />
      </Col>
      <Col className="col-auto p-0 pe-1">
        <ObjectsDropdown
          buttonRole="button"
          buttonAriaLabel={formatMessage({
            id: 'app.views.widgets.filters.advanced_filter.operator_selector_button.aria_label',
            defaultMessage: 'Operator selector',
          })}
          disabled={disabled}
          objects={conditionOperators}
          value={value.operator}
          onChange={handleChangeOperator}
          size={size}
        />
      </Col>
      <Col className="p-0">
        <ValueField
          disabled={disabled}
          suggestions={suggestionsForKey}
          tagsAreEqual={tagsAreEqual}
          values={value.values}
          onChange={handleChangeValues}
          minStrokes={minStrokes}
          numericKey={isNumericKey ? value.key : undefined}
          numericKeyDescription={
            isNumericKey ? numericKeys[value.key!] : undefined
          }
          singleValue={isRange}
        />
      </Col>
      <Col className="col-auto p-0 pe-1">
        {!disabled && (
          <Button
            role="button"
            aria-label={formatMessage({
              id: 'app.views.widgets.filters.advanced_filter.delete_this_condition_button.aria_label',
              defaultMessage: 'Delete this condition',
            })}
            onClick={handleDelete}
            outline
            color="light"
            size={size}
          >
            <i style={{ color: 'red' }} className="fe fe-trash-2" />
          </Button>
        )}
      </Col>
    </Row>
  );
};

// a typed value is accepted for a numeric key when it is a number
export const numericValueToFilterable = (
  key: string,
  keyDescription: string,
  text: string
): (Filterable & { name: string }) | null => {
  const trimmed = text.trim();
  if (trimmed === '' || Number.isNaN(Number(trimmed))) {
    return null;
  }
  return {
    name: trimmed,
    filterObject: { key, value: trimmed, keyDescription },
  };
};

export const ValueField: FC<{
  values: Filterable[];
  suggestions: Filterable[];
  tagsAreEqual?: (tag1: Filterable, tag2: Filterable) => boolean;
  onChange: (values: Filterable[]) => void;
  disabled: boolean;
  minStrokes?: number;
  // when set, a typed number is accepted as a value for this key in
  // addition to the suggestions
  numericKey?: string;
  numericKeyDescription?: string;
  // when true, only one value can be entered (e.g. the bound of a range)
  singleValue?: boolean;
}> = ({
  values,
  suggestions,
  tagsAreEqual,
  onChange,
  disabled,
  minStrokes,
  numericKey,
  numericKeyDescription,
  singleValue = false,
}) => {
  const { formatMessage } = useIntl();
  const allowNumericValue = !!numericKey;
  const renderCreateOption = useCallback(
    (text: string) =>
      numericValueToFilterable(
        numericKey ?? '',
        numericKeyDescription ?? '',
        text
      ),
    [numericKey, numericKeyDescription]
  );
  const includeFilterInput = useMemo(() => {
    return (
      <ReactTagsInput
        disabled={disabled}
        className={'w-100'}
        value={values}
        allowNew={allowNumericValue}
        renderCreateOption={renderCreateOption}
        maxTags={singleValue ? 1 : undefined}
        suggestions={suggestions}
        placeholder={formatMessage({
          id: 'app.views.widgets.filters.advanced_filter.placeholder.type_value_here',
          defaultMessage: 'Type value here...',
        })}
        callback={onChange}
        useTagCards={true}
        disableLengthCheck={true}
        tagsAreEqual={tagsAreEqual}
        minQueryLength={minStrokes}
      />
    );
  }, [
    allowNumericValue,
    disabled,
    formatMessage,
    minStrokes,
    onChange,
    renderCreateOption,
    singleValue,
    suggestions,
    tagsAreEqual,
    values,
  ]);

  return includeFilterInput;
};

const addGroup = (object: TGroup, depth: number): TGroup => {
  return {
    ...object,
    values: [
      ...object.values,
      {
        type: 'GROUP',
        operator: 'OR',
        values: [
          {
            type: 'CONDITION',
            key: undefined,
            values: [],
            operator: 'EQ',
            index: (depth + 2) * 100,
          },
        ],
        index:
          (maxBy(object.values, 'index')?.index ?? (depth + 1) * 100 - 1) + 1,
      },
    ],
  };
};

const addCondition = (
  object: TGroup,
  depth: number,
  selectedSuggestionConverter: (s: Filterable) => Filterable,
  initialValues: Filterable[] | undefined = [],
  removeEmpties: boolean | undefined = false
): TGroup => {
  return {
    ...object,
    values: [
      ...object.values.filter(
        (v) => !removeEmpties || (v.type === 'CONDITION' && !!v.key)
      ),
      {
        type: 'CONDITION',
        key: initialValues[0]?.filterObject?.key,
        values: initialValues.map((x) => selectedSuggestionConverter(x)),
        operator: 'EQ',
        index:
          (maxBy(object.values, 'index')?.index ?? (depth + 1) * 100 - 1) + 1,
      },
    ],
  };
};

const deleteItem = (object: TGroup, idx: number): TGroup => ({
  ...object,
  values: object.values.filter((v) => v.index !== idx),
});

const deleteNestedItem = (object: TGroup, idxToDelete: number): TGroup => {
  return {
    ...object,
    values: object.values
      .map((v) => {
        if (v.type === 'GROUP') {
          return deleteNestedItem(v, idxToDelete);
        }
        if (v.index === idxToDelete) {
          return null;
        }
        return v;
      })
      .filter((v) => v !== null) as (TCondition | TGroup)[],
  };
};

const validateCondition = (value: TCondition): 'CONDITION'[] => {
  const validationErrors: 'CONDITION'[] = [];
  if (!value.key || !value.values || value.values.length === 0) {
    validationErrors.push('CONDITION');
  }
  return validationErrors;
};

const validateGroup = (value: TGroup): ('GROUP' | 'CONDITION')[] => {
  const validationErrors: ('GROUP' | 'CONDITION')[] = [];

  if (value.values.length === 0) {
    validationErrors.push('GROUP');
  }
  value.values.forEach((v) => {
    if (v.type === 'CONDITION') {
      validationErrors.push(...validateCondition(v));
    } else {
      validationErrors.push(...validateGroup(v));
    }
  });
  return validationErrors;
};

export const isValidGroup = (value?: TGroup): boolean => {
  if (!value) {
    return true;
  }
  return validateGroup(value).length === 0;
};

const validate = (value: TGroup, intl: IntlShape): string[] => {
  const validationResult: ('GROUP' | 'CONDITION')[] = validateGroup(value);
  const validationErrors: string[] = [];

  if (validationResult.includes('CONDITION')) {
    validationErrors.push(
      intl.formatMessage({
        id: 'app.views.widgets.filters.advanced_filter.condition.required',
        defaultMessage: 'All conditions must be completed.',
      })
    );
  }
  if (validationResult.includes('GROUP')) {
    validationErrors.push(
      intl.formatMessage({
        id: 'app.views.widgets.filters.advanced_filter.group.required',
        defaultMessage: 'All groups must have at least one condition.',
      })
    );
  }
  return validationErrors;
};

const findConditionsNotMatchingSuggestions = (
  value: TGroup,
  suggestions: Filterable[],
  numericKeys: NumericKeys = NO_NUMERIC_KEYS
): TCondition[] => {
  if (!value || !value.values) {
    return [];
  }
  const invalidConditions: TCondition[] = [];
  value.values.forEach((v) => {
    if (v.type === 'CONDITION') {
      const availableKeys = new Set<string>([
        ...suggestions.map((s) => s.filterObject.key),
        ...Object.keys(numericKeys),
      ]);
      if (v.key && !availableKeys.has(v.key)) {
        invalidConditions.push(v);
      }
    } else {
      invalidConditions.push(
        ...findConditionsNotMatchingSuggestions(
          v as TGroup,
          suggestions,
          numericKeys
        )
      );
    }
  });
  return invalidConditions;
};

export default React.memo(AdvancedFilter);
